
import { Component, Vue, Watch } from 'vue-property-decorator';
import Gravatar from 'vue-gravatar';
import dayjs from 'dayjs';

import {
    ACCESS_LEVEL_LIMITED,
    ACCESS_LEVEL_UNRESTRICTED,
    DeviceInfo,
    getDefaultShareInfo,
    SharedDeviceInfo,
} from '@/types/device-info';
import { Action, State } from 'vuex-class';

import api from '@/modules/api';
import { UserInfo } from '@/types/user-info';
import DeviceSettingsShareMenu from '@/components/devices/menus/DeviceSettingsShareMenu.vue';
import ShareEditPopup from '@/components/devices/popups/ShareEditPopup.vue';
import * as dlg from '@/modules/dlg';
import { PRIVILEGES_OWNER, SECURITY_HIGH, SECURITY_NORMAL } from '@/utils/constants';
import { actions, mutations } from '@/store/types';
import { LoadDevicesActionType } from '@/types/store/actions';
import { getErrorMessage } from '@/utils/common';

@Component({
    components: { Gravatar, DeviceSettingsShareMenu, ShareEditPopup },
})
export default class DeviceSharingSettings extends Vue {
    shares: SharedDeviceInfo[] = [];
    share: SharedDeviceInfo | null = null;
    protected shareDialog = false;
    ManageSharingTitle = 'Manage sharing';
    tableLoading = false;

    @Action(actions.LOAD_DEVICES)
    loadDevices!: LoadDevicesActionType;

    @State('device')
    device!: DeviceInfo;

    @State('user')
    user!: UserInfo;

    async mounted() {
        this.refresh();
    }
    async refresh() {
        this.tableLoading = true;
        try {
            this.shares = await api.loadShare(this.device.deviceUUID);
        } catch (error) {
            this.tableLoading = false;
        } finally {
            this.tableLoading = false;
        }
    }
    async refreshDevices() {
        await this.loadDevices({ userId: this.user._id as string });
        // if (this.current && this.current._id) {
        //     await this.loadDevice({ deviceUUID: this.current.deviceUUID });
        // }
    }
    async add() {
        this.share = getDefaultShareInfo({
            deviceUUID: this.device.deviceUUID,
        });
    }
    async edit(share: SharedDeviceInfo) {
        this.share = { ...share };
    }
    async save(share: SharedDeviceInfo) {
        this.$store.commit(mutations.LOADING, { loading: true });
        if (share._id) {
            try {
                await api.updateShare({
                    ...share,
                    isOwnerFlag: share.accessRightsFlag === PRIVILEGES_OWNER ? 1 : 0,
                });
                this.refresh();
                this.refreshDevices();
                this.share = null;
                Vue.toasted.success('Updated successfully');
            } catch (error) {
                Vue.toasted.error(getErrorMessage(error));
            } finally {
                this.$store.commit(mutations.LOADING, { loading: false });
            }
        } else {
            try {
                await api.createShare({
                    ...share,
                    masterKey: this.device.passcode,
                    isOwnerFlag: share.accessRightsFlag === PRIVILEGES_OWNER ? 1 : 0,
                });
                this.refresh();
                this.refreshDevices();
                this.share = null;
                Vue.toasted.success('Shared successfully');
            } catch (error) {
                Vue.toasted.error(getErrorMessage(error));
            } finally {
                this.$store.commit(mutations.LOADING, { loading: false });
            }
        }
    }
    async remove(share: SharedDeviceInfo) {
        if (!share._id) {
            return;
        }
        const confirmed = await dlg.confirm('Are you sure you want to delete share?', 'Share removal');
        if (!confirmed) {
            return;
        }
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.deleteShare(share._id);
            await this.refresh();
            await this.refreshDevices();
            this.share = null;
            Vue.toasted.success('Removed successfully');
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }
    async resend(share: SharedDeviceInfo) {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.resendShare(share);
            // this.share = null;
            Vue.toasted.success('Shared successfully');
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    accepted(share: SharedDeviceInfo) {
        return !!share.isAcceptedFlag;
    }

    security(share: SharedDeviceInfo) {
        switch (share.securityLevel) {
            case SECURITY_HIGH:
                return 'High';
            case SECURITY_NORMAL:
            default:
                return 'Normal';
        }
    }

    privileges(share: SharedDeviceInfo) {
        if (share.isOwnerFlag) {
            return 'Owner';
        } else if (share.accessRightsFlag === ACCESS_LEVEL_UNRESTRICTED) {
            return 'Unrestricted';
        } else if (share.accessRightsFlag === ACCESS_LEVEL_LIMITED) {
            return 'Limited';
        }

        return '?????';
    }

    expiresIn(share: SharedDeviceInfo) {
        if (share.accessRightsFlag === ACCESS_LEVEL_LIMITED) {
            return dayjs(share.expiryDate).format('DD/MM/YYYY h:mm A');
        }

        return '-';
    }

    @Watch('device', { immediate: true, deep: true })
    onDeviceChanged(/*device: DeviceInfo*/) {
        // console.log(`>> Tab updated from watcher ${this.constructor.name}`);
        // if (device) {
        // }
    }
}
